import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Rss } from 'lucide-react';
import api from '../../lib/api';
import Button from '../../components/Button';
import {
  AddIcon,
  DeleteIcon,
  EditIcon,
  ErrorIcon,
  OpenExternalIcon,
  RefreshIcon,
  SuccessIcon,
  WarningIcon,
} from '../../components/icons';
import ActionsHeaderHelp from '../../components/ActionsHeaderHelp';
import StatCard, { StatCardRow } from '../../components/StatCard';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
  EmptyTableState,
  LoadingTableState,
} from '../../components/StandardTable';
import ToggleSwitch from '@shared/ui/ToggleSwitch';
import AdminModal from '../components/AdminModal';
import AdminPageShell from '../components/AdminPageShell';
import { HelpTrigger } from '../../help';
import { formatDateTime } from '../lib/format';

/**
 * News Feeds — the RSS catalog behind the dashboard news widget.
 *
 * Before this tool the widget hardcoded two feed URLs in the backend and
 * seeded them per user on every load, so changing the news meant a deploy.
 * The catalog is global: one row per feed, one on/off toggle, one fetch.
 *
 * Every feed is free today. `access_tier` exists on the row and is shown as a
 * column so the gating point is visible, but the editor does not offer a
 * choice yet — that is a product decision nobody has made.
 */

interface NewsFeed {
  id: string;
  name: string;
  url: string;
  publisher: string | null;
  category: string;
  region: string;
  description: string | null;
  homepage_url: string | null;
  is_enabled: boolean;
  access_tier: 'free' | 'premium';
  sort_order: number;
  last_fetched: string | null;
  last_error: string | null;
  last_item_count: number | null;
  item_count: number;
}

interface TestResult {
  ok: boolean;
  title?: string | null;
  itemCount?: number;
  latestTitle?: string | null;
  error?: string;
}

const CATEGORIES = ['politics', 'news', 'opinion', 'government'];
const REGIONS = [
  'statewide',
  'denver-metro',
  'boulder',
  'northern-colorado',
  'southern-colorado',
  'western-slope',
  'national',
];

const COLUMN_COUNT = 7;
const inputClasses = 'input';
const labelClasses = 'mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary';

const apiError = (error: any, fallback: string) =>
  error?.response?.data?.error ?? error?.response?.data?.errors?.[0]?.msg ?? fallback;

interface FeedForm {
  name: string;
  url: string;
  publisher: string;
  category: string;
  region: string;
  description: string;
  homepage_url: string;
  sort_order: number;
}

const emptyForm = (): FeedForm => ({
  name: '',
  url: '',
  publisher: '',
  category: 'politics',
  region: 'statewide',
  description: '',
  homepage_url: '',
  sort_order: 100,
});

const formFromFeed = (feed: NewsFeed): FeedForm => ({
  name: feed.name,
  url: feed.url,
  publisher: feed.publisher ?? '',
  category: feed.category,
  region: feed.region,
  description: feed.description ?? '',
  homepage_url: feed.homepage_url ?? '',
  sort_order: feed.sort_order,
});

const NewsFeedsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FeedForm>(emptyForm);
  const [testResults, setTestResults] = useState<Record<string, TestResult>>({});

  const feeds = useQuery({
    queryKey: ['admin-news-feeds'],
    queryFn: async () => {
      const response = await api.get('/admin/news-feeds');
      return (response.data.data ?? []) as NewsFeed[];
    },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin-news-feeds'] });

  const buildBody = () => ({
    name: form.name.trim(),
    url: form.url.trim(),
    publisher: form.publisher.trim() || null,
    category: form.category,
    region: form.region,
    description: form.description.trim() || null,
    homepage_url: form.homepage_url.trim() || null,
    sort_order: form.sort_order,
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      editingId
        ? api.put(`/admin/news-feeds/${editingId}`, buildBody())
        : api.post('/admin/news-feeds', buildBody()),
    onSuccess: () => {
      toast.success(editingId ? 'Feed updated' : 'Feed added — switch it on when you are ready');
      setEditorOpen(false);
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to save feed')),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isEnabled }: { id: string; isEnabled: boolean }) =>
      api.put(`/admin/news-feeds/${id}/enabled`, { is_enabled: isEnabled }),
    onSuccess: (response, vars) => {
      const fetchError = response.data?.fetchError;
      if (vars.isEnabled && fetchError) {
        toast.error(`Switched on, but the fetch failed: ${fetchError}`);
      } else {
        toast.success(vars.isEnabled ? 'Feed switched on' : 'Feed switched off');
      }
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to update feed')),
  });

  const testMutation = useMutation({
    mutationFn: async ({ id, url }: { id: string; url: string }) => {
      const response = await api.post('/admin/news-feeds/test', { url });
      return { id, result: response.data.data as TestResult };
    },
    onSuccess: ({ id, result }) => {
      setTestResults((cur) => ({ ...cur, [id]: result }));
      if (result.ok) {
        toast.success(`Feed works — ${result.itemCount} articles available`);
      } else {
        toast.error(`Feed failed: ${result.error}`);
      }
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to test feed')),
  });

  const refreshAllMutation = useMutation({
    mutationFn: () => api.post('/admin/news-feeds/refresh'),
    onSuccess: (response) => {
      const { feeds: total, succeeded, failed } = response.data.data ?? {};
      toast.success(
        failed > 0
          ? `Refreshed ${succeeded} of ${total} feeds — ${failed} failed`
          : `Refreshed ${succeeded} feeds`,
      );
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to refresh feeds')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/news-feeds/${id}`),
    onSuccess: () => {
      toast.success('Feed removed');
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to remove feed')),
  });

  const all = useMemo(() => feeds.data ?? [], [feeds.data]);

  const rows = useMemo(
    () =>
      all.filter((feed) => {
        if (statusFilter === 'enabled' && !feed.is_enabled) return false;
        if (statusFilter === 'disabled' && feed.is_enabled) return false;
        if (statusFilter === 'failing' && !feed.last_error) return false;
        if (regionFilter !== 'all' && feed.region !== regionFilter) return false;
        return true;
      }),
    [all, statusFilter, regionFilter],
  );

  const stats = useMemo(
    () => ({
      enabled: all.filter((f) => f.is_enabled).length,
      total: all.length,
      articles: all.reduce((sum, f) => sum + f.item_count, 0),
      failing: all.filter((f) => f.is_enabled && f.last_error).length,
    }),
    [all],
  );

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setEditorOpen(true);
  };
  const openEdit = (feed: NewsFeed) => {
    setEditingId(feed.id);
    setForm(formFromFeed(feed));
    setEditorOpen(true);
  };

  const saveDisabled = !form.name.trim() || !/^https?:\/\//i.test(form.url.trim());

  const confirmDelete = (feed: NewsFeed) => {
    if (
      window.confirm(
        `Remove "${feed.name}" from the catalog? Its ${feed.item_count} stored articles go too.`,
      )
    ) {
      deleteMutation.mutate(feed.id);
    }
  };

  return (
    <AdminPageShell
      title="News Feeds"
      description="The Colorado news sources behind the dashboard news widget"
      actions={
        <div className="flex items-center gap-2">
          <HelpTrigger contentKey="admin-news-feeds" size="md" ariaLabel="Help for news feeds" />
          <Button
            variant="secondary"
            size="sm"
            icon={RefreshIcon}
            loading={refreshAllMutation.isLoading}
            onClick={() => refreshAllMutation.mutate()}
          >
            Refresh All
          </Button>
          <Button variant="primary" size="sm" icon={AddIcon} onClick={openCreate}>
            Add Feed
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <StatCardRow>
          <StatCard label="Feeds on" value={stats.enabled} variant="success" loading={feeds.isLoading} />
          <StatCard label="In catalog" value={stats.total} loading={feeds.isLoading} />
          <StatCard label="Articles stored" value={stats.articles} variant="info" loading={feeds.isLoading} />
          <StatCard
            label="Failing"
            value={stats.failing}
            variant={stats.failing > 0 ? 'danger' : 'default'}
            sublabel={stats.failing > 0 ? 'switched on but not fetching' : undefined}
            loading={feeds.isLoading}
          />
        </StatCardRow>

        <div className="flex flex-wrap items-center gap-2">
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            aria-label="Filter by status"
            className="input h-9 w-auto py-0"
          >
            <option value="all">All feeds</option>
            <option value="enabled">Switched on</option>
            <option value="disabled">Switched off</option>
            <option value="failing">Failing</option>
          </select>
          <select
            value={regionFilter}
            onChange={(event) => setRegionFilter(event.target.value)}
            aria-label="Filter by coverage area"
            className="input h-9 w-auto py-0"
          >
            <option value="all">All coverage areas</option>
            {REGIONS.map((region) => (
              <option key={region} value={region}>
                {region.replace(/-/g, ' ')}
              </option>
            ))}
          </select>
        </div>

        {feeds.isError ? (
          <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
            <WarningIcon className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
            <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
              Couldn&apos;t load the news feed catalog.
            </p>
            <Button variant="outline" size="sm" onClick={() => feeds.refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          <StandardTable horizontalScroll loading={feeds.isFetching && !feeds.isLoading}>
            <StandardTableHeader>
              <StandardTableRow>
                <StandardTableHeaderCell>On</StandardTableHeaderCell>
                <StandardTableHeaderCell>Feed</StandardTableHeaderCell>
                <StandardTableHeaderCell responsiveHide="lg">Coverage</StandardTableHeaderCell>
                <StandardTableHeaderCell>Last fetch</StandardTableHeaderCell>
                <StandardTableHeaderCell responsiveHide="md">Articles</StandardTableHeaderCell>
                <StandardTableHeaderCell responsiveHide="lg">Access</StandardTableHeaderCell>
                <StandardTableHeaderCell>
                  <span className="inline-flex items-center gap-1.5">
                    Actions
                    <ActionsHeaderHelp icons={['edit', 'open-external', 'delete']} />
                  </span>
                </StandardTableHeaderCell>
              </StandardTableRow>
            </StandardTableHeader>
            <StandardTableBody>
              {feeds.isLoading ? (
                <LoadingTableState colSpan={COLUMN_COUNT} />
              ) : rows.length === 0 ? (
                <EmptyTableState
                  colSpan={COLUMN_COUNT}
                  message="No feeds match this filter"
                  icon={<Rss className="h-8 w-8" />}
                />
              ) : (
                rows.map((feed) => {
                  const test = testResults[feed.id];
                  return (
                    <StandardTableRow key={feed.id}>
                      <StandardTableCell>
                        <ToggleSwitch
                          checked={feed.is_enabled}
                          disabled={toggleMutation.isLoading}
                          label={`${feed.is_enabled ? 'Switch off' : 'Switch on'} ${feed.name}`}
                          onChange={(next) => toggleMutation.mutate({ id: feed.id, isEnabled: next })}
                        />
                      </StandardTableCell>
                      <StandardTableCell>
                        <span className="font-medium text-text-primary dark:text-text-dark-primary">
                          {feed.name}
                        </span>
                        {feed.publisher && (
                          <span className="block text-xs text-text-tertiary dark:text-text-dark-tertiary">
                            {feed.publisher}
                          </span>
                        )}
                        {feed.description && (
                          <span className="mt-0.5 block max-w-md text-xs text-text-secondary dark:text-text-dark-secondary">
                            {feed.description}
                          </span>
                        )}
                        {test && (
                          <span
                            className={`mt-1 flex items-center gap-1 text-xs ${
                              test.ok
                                ? 'text-success-700 dark:text-success-400'
                                : 'text-error-700 dark:text-error-400'
                            }`}
                          >
                            {test.ok ? (
                              <SuccessIcon className="h-3.5 w-3.5" />
                            ) : (
                              <ErrorIcon className="h-3.5 w-3.5" />
                            )}
                            {test.ok
                              ? `Tested from server: ${test.itemCount} articles`
                              : `Tested from server: ${test.error}`}
                          </span>
                        )}
                      </StandardTableCell>
                      <StandardTableCell responsiveHide="lg">
                        <span className="capitalize">{feed.region.replace(/-/g, ' ')}</span>
                        <span className="block text-xs capitalize text-text-tertiary dark:text-text-dark-tertiary">
                          {feed.category}
                        </span>
                      </StandardTableCell>
                      <StandardTableCell>
                        {feed.last_error ? (
                          <span className="flex items-start gap-1 text-xs text-error-700 dark:text-error-400">
                            <ErrorIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span className="max-w-[16rem] break-words">{feed.last_error}</span>
                          </span>
                        ) : feed.last_fetched ? (
                          <span className="text-xs text-text-secondary dark:text-text-dark-secondary">
                            {formatDateTime(feed.last_fetched)}
                          </span>
                        ) : (
                          <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                            Never fetched
                          </span>
                        )}
                      </StandardTableCell>
                      <StandardTableCell responsiveHide="md">{feed.item_count}</StandardTableCell>
                      <StandardTableCell responsiveHide="lg">
                        <span className="inline-flex rounded-full bg-success-100 px-2 py-0.5 text-xs font-medium text-success-800 dark:bg-success-900 dark:bg-opacity-40 dark:text-success-300">
                          {feed.access_tier}
                        </span>
                      </StandardTableCell>
                      <StandardTableCell>
                        <div className="flex items-center gap-1.5">
                          <Button
                            variant="outline"
                            size="sm"
                            loading={testMutation.isLoading && testMutation.variables?.id === feed.id}
                            onClick={() => testMutation.mutate({ id: feed.id, url: feed.url })}
                          >
                            Test
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={EditIcon}
                            aria-label={`Edit ${feed.name}`}
                            title={`Edit ${feed.name}`}
                            onClick={() => openEdit(feed)}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={OpenExternalIcon}
                            aria-label={`Open ${feed.name} feed URL`}
                            title={`Open ${feed.name} feed URL`}
                            onClick={() => window.open(feed.url, '_blank', 'noopener,noreferrer')}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={DeleteIcon}
                            aria-label={`Remove ${feed.name}`}
                            title={`Remove ${feed.name}`}
                            onClick={() => confirmDelete(feed)}
                          />
                        </div>
                      </StandardTableCell>
                    </StandardTableRow>
                  );
                })
              )}
            </StandardTableBody>
          </StandardTable>
        )}

        <AdminModal
          isOpen={editorOpen}
          title={editingId ? 'Edit Feed' : 'Add Feed'}
          subtitle="Adding a feed does not switch it on — use the toggle in the list."
          onClose={() => setEditorOpen(false)}
          footer={
            <>
              <Button variant="secondary" size="sm" onClick={() => setEditorOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={saveDisabled}
                loading={saveMutation.isLoading}
                onClick={() => saveMutation.mutate()}
              >
                {editingId ? 'Save Changes' : 'Add Feed'}
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <div>
              <label htmlFor="feed-name" className={labelClasses}>
                Name *
              </label>
              <input
                id="feed-name"
                type="text"
                value={form.name}
                placeholder="Colorado Sun — Politics"
                onChange={(event) => setForm((cur) => ({ ...cur, name: event.target.value }))}
                className={inputClasses}
              />
            </div>
            <div>
              <label htmlFor="feed-url" className={labelClasses}>
                Feed URL *
              </label>
              <input
                id="feed-url"
                type="url"
                value={form.url}
                placeholder="https://example.com/category/politics/feed/"
                onChange={(event) => setForm((cur) => ({ ...cur, url: event.target.value }))}
                className={inputClasses}
              />
              <div className="mt-1.5 flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!/^https?:\/\//i.test(form.url.trim())}
                  loading={testMutation.isLoading && testMutation.variables?.id === 'editor'}
                  onClick={() => testMutation.mutate({ id: 'editor', url: form.url.trim() })}
                >
                  Test this URL
                </Button>
                {testResults.editor && (
                  <span
                    className={`text-xs ${
                      testResults.editor.ok
                        ? 'text-success-700 dark:text-success-400'
                        : 'text-error-700 dark:text-error-400'
                    }`}
                  >
                    {testResults.editor.ok
                      ? `${testResults.editor.title ?? 'Feed'} — ${testResults.editor.itemCount} articles`
                      : testResults.editor.error}
                  </span>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label htmlFor="feed-publisher" className={labelClasses}>
                  Publisher
                </label>
                <input
                  id="feed-publisher"
                  type="text"
                  value={form.publisher}
                  placeholder="The Colorado Sun"
                  onChange={(event) => setForm((cur) => ({ ...cur, publisher: event.target.value }))}
                  className={inputClasses}
                />
              </div>
              <div>
                <label htmlFor="feed-homepage" className={labelClasses}>
                  Homepage
                </label>
                <input
                  id="feed-homepage"
                  type="url"
                  value={form.homepage_url}
                  placeholder="https://example.com/"
                  onChange={(event) => setForm((cur) => ({ ...cur, homepage_url: event.target.value }))}
                  className={inputClasses}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label htmlFor="feed-category" className={labelClasses}>
                  Category
                </label>
                <select
                  id="feed-category"
                  value={form.category}
                  onChange={(event) => setForm((cur) => ({ ...cur, category: event.target.value }))}
                  className={inputClasses}
                >
                  {CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="feed-region" className={labelClasses}>
                  Coverage
                </label>
                <select
                  id="feed-region"
                  value={form.region}
                  onChange={(event) => setForm((cur) => ({ ...cur, region: event.target.value }))}
                  className={inputClasses}
                >
                  {REGIONS.map((region) => (
                    <option key={region} value={region}>
                      {region.replace(/-/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="feed-order" className={labelClasses}>
                  Sort order
                </label>
                <input
                  id="feed-order"
                  type="number"
                  min={0}
                  value={form.sort_order}
                  onChange={(event) =>
                    setForm((cur) => ({ ...cur, sort_order: Number(event.target.value) || 0 }))
                  }
                  className={inputClasses}
                />
              </div>
            </div>
            <div>
              <label htmlFor="feed-description" className={labelClasses}>
                Note
              </label>
              <textarea
                id="feed-description"
                rows={2}
                value={form.description}
                placeholder="What this feed covers, and anything worth knowing before switching it on."
                onChange={(event) => setForm((cur) => ({ ...cur, description: event.target.value }))}
                className={inputClasses}
              />
            </div>
            <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
              Every feed in the catalog is free to all users. Test the URL before switching a feed
              on — some publishers block server requests, and the test runs from the backend rather
              than from your browser.
            </p>
          </div>
        </AdminModal>
      </div>
    </AdminPageShell>
  );
};

export default NewsFeedsPage;
